
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Eye, EyeOff, Check, Info } from "lucide-react";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const signUpSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  displayName: z
    .string()
    .min(3, { message: "Display name must be at least 3 characters" })
    .max(30, { message: "Display name must be less than 30 characters" }),
  password: z
    .string()
    .min(6, { message: "Password must be at least 6 characters" }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type SignUpFormValues = z.infer<typeof signUpSchema>;

interface SignUpFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  displayName: string;
  setDisplayName: (displayName: string) => void;
  loading: boolean;
  onSubmit: (email: string, password: string, confirmPassword: string, displayName: string, e: React.FormEvent) => Promise<void>;
  validationError: string;
  setValidationError: (error: string) => void;
  confirmPassword: string;
  setConfirmPassword: (password: string) => void;
}

const SignUpForm = ({
  email,
  setEmail,
  password,
  setPassword,
  displayName,
  setDisplayName,
  loading,
  onSubmit,
  validationError,
  setValidationError,
  confirmPassword,
  setConfirmPassword
}: SignUpFormProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: email,
      displayName: displayName,
      password: password,
      confirmPassword: confirmPassword,
    },
  });

  // Only reset the form when the props change significantly
  // This avoids resetting on every keystroke
  useEffect(() => {
    // Only reset form when values come from external changes (not from typing)
    // We're checking if the current form values are empty but props have values
    const currentValues = form.getValues();
    const shouldUpdateForm = (
      (currentValues.email === '' && email !== '') ||
      (currentValues.displayName === '' && displayName !== '') ||
      (currentValues.password === '' && password !== '') ||
      (currentValues.confirmPassword === '' && confirmPassword !== '')
    );
    
    if (shouldUpdateForm) {
      form.reset({
        email,
        displayName,
        password,
        confirmPassword,
      });
    }
  }, [email, displayName, password, confirmPassword, form]);

  // Update parent state when form values *finish* changing, not during typing
  const handleFormSubmit = (data: SignUpFormValues) => {
    // Update parent state
    setEmail(data.email);
    setDisplayName(data.displayName);
    setPassword(data.password);
    setConfirmPassword(data.confirmPassword);
    
    // Clear any previous validation errors
    setValidationError("");
    
    // Create a synthetic event to pass to the original onSubmit
    const syntheticEvent = {
      preventDefault: () => {},
    } as React.FormEvent;
    
    // Pass form data directly to handler
    onSubmit(data.email, data.password, data.confirmPassword, data.displayName, syntheticEvent);
  };

  return (
    <>
      {validationError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email address</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    required
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Display Name <span className="text-xs text-gray-500">(required)</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    required
                    placeholder="Enter your display name"
                  />
                </FormControl>
                <FormDescription className="text-xs flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  3-30 characters, this is how others will see you
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showPassword ? "text" : "password"}
                      required
                    />
                    <button 
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </FormControl>
                <FormDescription className="text-xs flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Password must be at least 6 characters
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showConfirmPassword ? "text" : "password"}
                      required
                    />
                    <button 
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading ? "Creating account..." : "Create account"}
          </Button>
        </form>
      </Form>
    </>
  );
};

export default SignUpForm;
